import React, { ChangeEvent, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../reducers/store';
import { postValidateCategory, postValidateDescription, postValidateLink, postValidateSelectedFile, postValidateTitle } from '../../validations/posts/createPost';
import toast from 'react-hot-toast';
import { actionUpdatePost } from '../../../actions/posts';
import {FormField, Button, SubmitModal, Loader, ImageBuffer, SubCover} from '../../';
import { create } from '../../../assets';
import { PhotoRounded } from '@mui/icons-material';
import { allCategoryHeader } from '../../../utils/constants';

import './editPostDetails.css'

interface PostDataParams {
  title?: string;
  description?: string;
  category?: string;
  link?: string;
}

const EditPostDetails = ({id, details}: any) => {
  const dispatch                                    = useDispatch<AppDispatch>();
  const [savingInfo, setSavingInfo]                 = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled]     = useState<boolean>(true);
  const [openSubMegaMenu, setOpenSubMegaMenu]       = useState<boolean>(false);
  const [onOpen, setOnOpen]                         = useState<boolean>(false);
  const [userIdErrors, setUserIdErrors]             = useState<string[]>([]);
  const [titleErrors, setTitleErrors]               = useState<string[]>([]);
  const [descriptionErrors, setDescriptionErrors]   = useState<string[]>([]);
  const [categoryErrors, setCategoryErrors]         = useState<string[]>([]);
  const [linkErrors, setLinkErrors]                 = useState<string[]>([]);
  const [errorMessage, setErrorMessage]             = useState(null);

  const [postData, setPostData]                     = useState<PostDataParams>({title: details?.title || '', description: details?.description || '', category: details?.category || '', link: details?.link || ''})

  const [ isLoadingBtn, setIsLoadingBtn ]   = useState({title: false, description: false, category: false, link: false})

  const [ isValid, setIsValid ]             = useState({title: false, description: false, category: false, link: false})

  const useDebounce = <T,>(value: T, delay: number): T => {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
    const handler = setTimeout(() => {
        setDebounced(value);
    }, delay);
    return () => clearTimeout(handler);
    }, [value, delay]);

    return debounced;
  };

  const debouncedPostData = useDebounce<PostDataParams>(postData, 500);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setIsLoadingBtn((prevState) => ({ ...prevState, [name]: true }));
    setPostData((prevState) => ({ ...prevState, [name]: value }));
  }

  useEffect(() => {
    const validateTitle = () => {
        const { isValid, errors } = postValidateTitle({ postData: debouncedPostData });
        setIsValid((prevState) => ({
            ...prevState,
            title: isValid,
        }));
        setTitleErrors(errors);
        setIsLoadingBtn((prevState) => ({
            ...prevState,
            title: false,
        }));
        return errors;
    }

    const validateDescription = () => {
        const { isValid, errors } = postValidateDescription({ postData: debouncedPostData });
        setIsValid((prevState) => ({
            ...prevState,
            description: isValid,
        }));
        setDescriptionErrors(errors);
        setIsLoadingBtn((prevState) => ({
            ...prevState,
            description: false,
        }));
        return errors;
    }

    const validateCategory = () => {
        const { isValid, errors } = postValidateCategory({ postData: debouncedPostData });
        setIsValid((prevState) => ({
            ...prevState,
            category: isValid,
        }));
        setCategoryErrors(errors);
        setIsLoadingBtn((prevState) => ({
            ...prevState,
            category: false,
        }));
        return errors;
    }

    const validateLink = () => {
        const { isValid, errors } = postValidateLink({ postData: debouncedPostData });
        setIsValid((prevState) => ({
            ...prevState,
            link: isValid,
        }));
        setLinkErrors(errors);
        setIsLoadingBtn((prevState) => ({
            ...prevState,
            link: false,
        }));
        return errors;
    }

    const titleErrors         = validateTitle();
    const descriptionErrors   = validateDescription();
    const categoryErrors      = validateCategory();
    const linkErrors          = validateLink();

    setUserIdErrors(userIdErrors)
    setTitleErrors(titleErrors)
    setDescriptionErrors(descriptionErrors)
    setCategoryErrors(categoryErrors)
    setLinkErrors(linkErrors)

    const hasFormErrors = titleErrors.length > 0 || descriptionErrors.length > 0 || categoryErrors.length > 0 || linkErrors.length > 0;
    setIsButtonDisabled(hasFormErrors);

  }, [debouncedPostData])

  const handleModalSubmit = () => {
    setOnOpen(true);
    setOpenSubMegaMenu(true)
  }

  const handleCloseCover = () => {
    setOnOpen(false) // modal
    setOpenSubMegaMenu(false)
  }

  const handleSubmit = async(e: ChangeEvent<any>) => {
    e.preventDefault();
    handleModalSubmit();
  }

  const cancelPostCreation = () => {
    setOnOpen(false); // Close the modal
    setSavingInfo(false);
    setOpenSubMegaMenu(false)
  };

  const confirmPostCreation = async() => {
    setSavingInfo(true);
    setIsButtonDisabled(true);
    const res = await dispatch(actionUpdatePost({id, postData}))
    try{
      if (res.success === true) {
      // Password reset successful, show success message from response
      toast.success("post updated successfully");
      setErrorMessage(null); // Clear any previous error message
      setPostData({ title: res?.data?.title || '', description: res?.data?.description || '', category: res?.data?.category || '', link: res?.data?.link || '' })
      setSavingInfo(false)
      } else if(res.status === 400) {
        setErrorMessage(res?.data.message);
        setSavingInfo(false)
      }
    }catch(error){
      setErrorMessage(res.status)
      setSavingInfo(false)
    }
    setOnOpen(false); // Close the modal after confirmation
  };

  return (
    <>
      <title>Update Post</title> 
      <div className='edit-detail-admin-container'>
        <form className='' onSubmit={handleSubmit} autoComplete="off">
          <div className='edit-detail-admin-title'>
            <h3>update post details</h3>
          </div>

          <div className=''>
            <FormField inputType type="text" labelName="Title" name="title" value={postData.title} handleChange={handleChange} isLoadingBtn={isLoadingBtn.title} isValid={isValid.title} errors={titleErrors || []} />

            <FormField inputType type="text" labelName="Description" name="description" value={postData.description} handleChange={handleChange} isLoadingBtn={isLoadingBtn.description} isValid={isValid.description} maxLength={120} errors={descriptionErrors || []} />

            <FormField selectType renderOption={(option) => ({ value: option.hint, label: `${option.title}` })} labelName="Category" name="category" value={postData.category} handleChange={handleChange} options={allCategoryHeader} isLoadingBtn={isLoadingBtn.category} isValid={isValid.category} errors={categoryErrors || []} />

            <FormField inputType type="text" labelName="Link" name="link" value={postData.link} handleChange={handleChange} isLoadingBtn={isLoadingBtn.link} isValid={isValid.link} errors={linkErrors || []} />

            <SubmitModal onOpen={onOpen} onClose={cancelPostCreation} onConfirm={confirmPostCreation} modalImage={create} prompt="Are you sure you want to update post" />

            <Button onClickButton buttonClickWrap={savingInfo ? `button-login-submitted` : `button-login-submit`} onClickName={savingInfo ? <>{<Loader />} Updating...</> : "Update"} isButtonDisabled={isButtonDisabled} buttonClasses={savingInfo ? ['button-disabled'] : (isButtonDisabled ? ['buttonDisabledClass'] : ['buttonEnabledClass'])} disabled={savingInfo} />
            {errorMessage && <p className='error-msg'>{errorMessage}</p>}
          </div>
        </form>
      </div>

      {openSubMegaMenu && (
        //z-index: 3, others should be higher
        <SubCover handleCloseCover={handleCloseCover} />
      )}
    </>
  )
}

export default EditPostDetails